import {
    IcosahedronBufferGeometry,
    InstancedBufferGeometry,
    InstancedBufferAttribute,
    ShaderMaterial,
    Mesh,
    Vector3,
    UniformsLib,
    ShaderLib,
    ClampToEdgeWrapping,
    Vector2,
    Vector4
} from 'three/build/three.module';

import { GPUComputationRenderer } from 'three/examples/jsm/misc/GPUComputationRenderer';
import FragmentShaderPosition from '../shaders/FragmentShaderPosition';
import FragmentShaderVelocity from '../shaders/FragmentShaderVelocity';
// import FlowField from './textures/FlowField';
import StartingPositions from './textures/StartingPositions';
import AngleTexture from './textures/AngleTexture';
// Phase 8 tuning scaffold (TEMPORARY — remove at sign-off): live aesthetic-lever store.
import labTuning from '../../../../lib/labTuning';

import ParticleSystemVertexShader from '../shaders/SoulEater/ParticleSystemMeshShader/ParticleSystemVertexShader';
import ParticleSystemFragmentShader from '../shaders/SoulEater/ParticleSystemMeshShader/ParticleSystemFragmentShader';

import ParticleSystemDepthVertexShader from '../shaders/SoulEater/ParticleSystemDepthShader/ParticleSystemDepthVertexShader';
import ParticleSystemDepthFragmentShader from '../shaders/SoulEater/ParticleSystemDepthShader/ParticleSystemDepthFragmentShader';

// Per-particle mass range. Heavier particles are visually larger, more strongly
// attracted to the attractor, and can reach higher max speeds.
const MASS_MIN = 0.75;
const MASS_MAX = 1.25;

// On each page load, randomly choose between uniform mass (all 1.0) or
// per-particle random mass in [MASS_MIN, MASS_MAX].
const USE_RANDOM_MASS = Math.random() < 0.5;

// LAB lifecycle max age (Phase 3B). A SINGLE shared uniform, NOT a per-particle texture
// value: the position shader's per-material read of ForceTexture.w proved unreliable
// (respawn never fired even though render-side aging clearly reached maxAge — the position
// material's custom-sampler read returned a value age never reaches), so maxAge is now a
// plain `uLabMaxAge` uniform fed identically to the respawn (FragmentShaderPosition) and the
// fade (ParticleSystemVertexShader). Per-particle variety still comes from the random initial
// age (fillPositionTexture) + the per-particle lifeSpeed (read reliably via velocity.w), so
// cycles desynchronise. Lifespan ≈ uLabMaxAge / lifeSpeed frames → with lifeSpeed 0.008–0.018
// ≈ 2.8–6.3 s. Tunable. LAB-only (gated by uLabBlend); age only advances in LAB.
const LAB_MAXAGE = 3.0;

class ParticleSystem {

    constructor(radius, w_, r, renderer) {

        this.circleGeometry = new IcosahedronBufferGeometry(4, 0);

        this.geometry = new InstancedBufferGeometry();
        this.geometry.index = this.circleGeometry.index;
        this.geometry.attributes = this.circleGeometry.attributes;

        this.angletexture = new AngleTexture(r);

        const values = this.initValues(w_, r);

        this.geometry.setAttribute('uvs', new InstancedBufferAttribute(values.uv, 2));

        this.uniforms = {
            ...UniformsLib.shadowmap,
            ...ShaderLib.phong.uniforms,
            time: { value: 0.0 },
            near: { value: 0.0 },
            far: { value: 300.0 },
            radius: { value: radius },
            noiseAmount: { value: radius / 2 },
            pointSize: { value: 3.0 },
            particleScale: { value: 1.0 },
            forceTexture: { value: null },
            pColor: { value: new Vector3(0.5, 0.5, 0.5) },
            texturePosition: { value: null },
            textureVelocity: { value: null },
            startingPosition: { value: null },
            // LAB lifecycle fade (Phase 3B). Mirrored from the sim each frame in update();
            // the vertex shader scales particles by normalized age only when uLabBlend > 0.
            uLabBlend: { value: 0.0 },
            uLabReducedMotion: { value: 0.0 },
            // Shared lifecycle max age (constant). Same value drives the position-shader
            // respawn so fade-to-death and respawn stay in lock-step (no dead/over-aged window).
            uLabMaxAge: { value: LAB_MAXAGE },
            // LAB look (Phase 8R). Tier identity + size hierarchy + fragment look levers.
            // All fragment/vertex consumers are uLabBlend-gated -> Home byte-identical at 0.
            // uLabDarkPass: 1 only while the bloom dark pass renders (getDark/restoreColor),
            // letting the sparse anchor tier alone seed UnrealBloomPass in LAB.
            uLabDarkPass: { value: 0.0 },
            uLabSimRes: { value: r },
            uLabTierFrac: { value: new Vector2(0.55, 0.98) },
            uLabSizeLo: { value: new Vector3(0.55, 1.0, 1.65) },
            uLabSizeHi: { value: new Vector3(0.95, 1.7, 2.75) },
            uLabLum: { value: new Vector3(0.32, 0.46, 0.64) },
            uLabDisc: { value: new Vector2(0.25, 0.85) },
            uLabDepthDim: { value: new Vector3(0.0, 1.0, 0.5) },
            uLabAnchorGlow: { value: 0.45 },
            depthWrite: true,
            depthTest: true
        };

        this.material = new ShaderMaterial({
            uniforms: this.uniforms,
            lights: true,
            vertexShader: ParticleSystemVertexShader,
            fragmentShader: ParticleSystemFragmentShader,
            depthTest: true,
            depthWrite: true
        });

        this.mesh = new Mesh(this.geometry, this.material);
        // this.mesh.rotateX(Math.PI / 2);
        this.mesh.translateX(-window.innerWidth/2);
        this.mesh.translateY(-window.innerHeight/2);
        this.mesh.customDepthMaterial = new ShaderMaterial({
            uniforms: this.uniforms,
            vertexShader: ParticleSystemDepthVertexShader,
            fragmentShader: ParticleSystemDepthFragmentShader
        });
        this.mesh.castShadow = true;
        this.mesh.receiveShadow = true;
        this.mesh.frustumCulled = false;
        this.mesh.name = 'DarkMatterParticles';

        this.initComputeRenderer(r, renderer, radius);

    }

    initComputeRenderer = (resolution, renderer, radius) => {

        // initialization
        // Create computational renderer
        this.gpuCompute = new GPUComputationRenderer(resolution, resolution, renderer);

        // Create initial state float textures
        let dtPosition = this.gpuCompute.createTexture();
        let dtVelocity = this.gpuCompute.createTexture();
        let dtForce = this.gpuCompute.createTexture();

        // Fill in with data
        this.fillPositionTexture(dtPosition);
        this.fillVelocityTexture(dtVelocity);
        this.fillForceTexture(dtForce);

        this.velocityVariable = this.gpuCompute.addVariable("textureVelocity", FragmentShaderVelocity, dtVelocity);
        this.positionVariable = this.gpuCompute.addVariable("texturePosition", FragmentShaderPosition, dtPosition);

        this.gpuCompute.setVariableDependencies(this.velocityVariable, [this.positionVariable, this.velocityVariable]);
        this.gpuCompute.setVariableDependencies(this.positionVariable, [this.positionVariable, this.velocityVariable]);

        this.positionUniforms = this.positionVariable.material.uniforms;
        this.velocityUniforms = this.velocityVariable.material.uniforms;

        const startingPositions = new StartingPositions(resolution);

        // Simulation uniforms
        this.positionUniforms["time"] = { value: 0.0 };
        this.positionUniforms["screenResolution"] = { value: new Vector2(window.innerWidth, window.innerHeight) };
        this.positionUniforms["startingPosition"] = { value: startingPositions.texture };
        this.positionUniforms["isManifesto"] = { value: false };
        // LAB lifecycle (Phase 3B). Respawn inputs for the position shader; gated by uLabBlend.
        // The respawn target (cloud home) and maxAge are NOT sampled textures: the home is
        // computed inline in the shader (labCloudHome) and maxAge is the shared uLabMaxAge
        // uniform — the position material's custom-sampler reads (ForceTexture / labOrigin)
        // proved unreliable. uLabReducedMotion freezes the lifecycle under reduced motion.
        this.positionUniforms["uLabBlend"] = { value: 0.0 };
        this.positionUniforms["uLabReducedMotion"] = { value: 0.0 };
        this.positionUniforms["uLabMaxAge"] = { value: LAB_MAXAGE };
        // LAB composition (Phase 8R — lobed volumetric cloud). labCloudHome reads these on BOTH
        // sim materials; real values are CPU-derived from the labTuning meta levers by
        // pushLabComposition() (seeded right after both uniform sets exist, re-pushed every update).
        this.positionUniforms["uLabLobeA"] = { value: new Vector4() };
        this.positionUniforms["uLabLobeB"] = { value: new Vector4() };
        this.positionUniforms["uLabLobeC"] = { value: new Vector4() };
        this.positionUniforms["uLabLobeD"] = { value: new Vector4() };
        this.positionUniforms["uLabLobeW"] = { value: new Vector4() };
        this.positionUniforms["uLabShape"] = { value: new Vector4() };
        this.positionUniforms["uLabArm"] = { value: new Vector3() };
        this.positionUniforms["uLabVoid"] = { value: new Vector2(0.32, 0.20) };
        this.positionUniforms["uLabTierZ"] = { value: new Vector2(0.45, 0.18) };
        this.positionUniforms["uLabTierFrac"] = { value: new Vector2(0.55, 0.98) };

        this.velocityUniforms["time"] = { value: 0.0 };
        this.velocityUniforms["ForceTexture"] = { value: dtForce };
        this.velocityUniforms["screenResolution"] = { value: new Vector2(window.innerWidth, window.innerHeight) };
        this.velocityUniforms["origin"] = { value: new Vector3(0.0, 0.0, 0.0) };
        this.velocityUniforms["originMass"] = { value: 20.0 };
        this.velocityUniforms["gravity"] = { value: 2.0 };
        this.velocityUniforms["radius"] = { value: 40.0 };
        this.velocityUniforms["angles"] = { value: this.angletexture.texture };
        this.velocityUniforms["isManifesto"] = { value: false };
        this.velocityUniforms["isMobile"] = { value: true };
        this.velocityUniforms["velocityClamp"] = { value: 5.0 };
        this.velocityUniforms["center"] = { value: new Vector2(window.innerWidth*0.5, window.innerHeight*0.5 ) };
        // LAB autonomous-field blend. Ramped by Sketch.update() and read by the velocity
        // shader's curl branch; at 0 the branch is skipped → default behavior unchanged.
        this.velocityUniforms["uLabBlend"] = { value: 0.0 };
        // LAB curl drift strength (Phase 3A). Set each frame by Sketch.update()
        // (0 under reduced motion). Init 0 so there is no drift until LAB is entered.
        this.velocityUniforms["uLabFlowStrength"] = { value: 0.0 };
        // LAB reduced-motion flag (Phase 3B). Set by Sketch.update(); the single source
        // value, fanned out to the position + render materials in update() below.
        this.velocityUniforms["uLabReducedMotion"] = { value: 0.0 };
        // LAB composition + motion (Phase 8R — lobed volumetric cloud). Composition uniforms are
        // declared identically on the position material (same labCloudHome); values are CPU-derived
        // from labTuning by pushLabComposition() below. Containment/noise stay velocity-only.
        this.velocityUniforms["uLabLobeA"] = { value: new Vector4() };
        this.velocityUniforms["uLabLobeB"] = { value: new Vector4() };
        this.velocityUniforms["uLabLobeC"] = { value: new Vector4() };
        this.velocityUniforms["uLabLobeD"] = { value: new Vector4() };
        this.velocityUniforms["uLabLobeW"] = { value: new Vector4() };
        this.velocityUniforms["uLabShape"] = { value: new Vector4() };
        this.velocityUniforms["uLabArm"] = { value: new Vector3() };
        this.velocityUniforms["uLabVoid"] = { value: new Vector2(0.32, 0.20) };
        this.velocityUniforms["uLabTierZ"] = { value: new Vector2(0.45, 0.18) };
        this.velocityUniforms["uLabTierFrac"] = { value: new Vector2(0.55, 0.98) };
        this.velocityUniforms["uLabContainment"] = { value: 0.0050 };
        this.velocityUniforms["uLabNoise"] = { value: new Vector2(0.0015, 0.05) };

        // Seed the derived lobe values immediately (gpuCompute.init() runs the shaders once;
        // everything is uLabBlend-gated, but real values from frame zero keep it deterministic).
        this.pushLabComposition();

        // wraS and wrapT of simulation textures
        this.velocityVariable.wrapS = ClampToEdgeWrapping;
        this.velocityVariable.wrapT = ClampToEdgeWrapping;
        this.positionVariable.wrapS = ClampToEdgeWrapping;
        this.positionVariable.wrapT = ClampToEdgeWrapping;

        let error = this.gpuCompute.init();

        if (error !== null) {
            console.error(error);
        }

        // Expose the force texture to the mesh shader so per-particle mass
        // can drive visual size.
        this.uniforms['forceTexture'] = { value: dtForce };

    }

    // Phase 8R-B: derive the four lobe vec4s + weights from the labTuning meta levers and push
    // them to BOTH sim materials. Golden-angle layout (baked base rotation), +/-18% sine-staggered
    // spread, geometric radius falloff (lobeAsym^i), staggered z, selection weights ~ radius^2
    // (bigger lobes get proportionally more particles -> uniform apparent density).
    pushLabComposition = () => {
        const _T = labTuning;
        const _pu = this.positionUniforms, _vu = this.velocityUniforms;
        const GOLDEN_ANGLE = 2.39996;
        const LOBE_ANGLE0 = 0.6;   // baked base rotation of the lobe layout
        const LOBE_NAMES = ["uLabLobeA", "uLabLobeB", "uLabLobeC", "uLabLobeD"];
        const ws = [0, 0, 0, 0];
        for (let i = 0; i < 4; i++) {
            const a = LOBE_ANGLE0 + i * GOLDEN_ANGLE;
            const sp = _T.lobeSpread * (1.0 + 0.18 * Math.sin(i * 2.1));
            const r = _T.lobeScale * Math.pow(_T.lobeAsym, i);
            const cx = Math.cos(a) * sp;
            const cy = Math.sin(a) * sp * 0.72;   // flattened ring of centers (matches yFlatten)
            const cz = _T.lobeZNear - (i / 3) * _T.lobeZSpan;
            _pu[LOBE_NAMES[i]].value.set(cx, cy, cz, r);
            _vu[LOBE_NAMES[i]].value.set(cx, cy, cz, r);
            ws[i] = r * r;
        }
        _pu["uLabLobeW"].value.set(ws[0], ws[1], ws[2], ws[3]);
        _vu["uLabLobeW"].value.set(ws[0], ws[1], ws[2], ws[3]);
        _pu["uLabShape"].value.set(0.72, _T.falloffPow, _T.driftAmp, _T.driftFreq);
        _vu["uLabShape"].value.set(0.72, _T.falloffPow, _T.driftAmp, _T.driftFreq);
        _pu["uLabArm"].value.set(_T.armFrac, 0.04, _T.armBulge);
        _vu["uLabArm"].value.set(_T.armFrac, 0.04, _T.armBulge);
        _pu["uLabVoid"].value.set(_T.voidX, _T.voidY);
        _vu["uLabVoid"].value.set(_T.voidX, _T.voidY);
        // Tier constants (dust z push, anchor z pull) + fractions shared with the render tiers.
        _pu["uLabTierZ"].value.set(0.45, 0.18);
        _vu["uLabTierZ"].value.set(0.45, 0.18);
        _pu["uLabTierFrac"].value.set(_T.dustFrac, 1.0 - _T.anchorFrac);
        _vu["uLabTierFrac"].value.set(_T.dustFrac, 1.0 - _T.anchorFrac);
    }

    // Bloom dark pass (called every frame around bloomComposer.render()). Phase 8R: also
    // flips uLabDarkPass so the LAB anchor tier can seed the bloom; .set() instead of
    // `new Vector3` kills a per-frame allocation (same object, Sketch's pColor override
    // already mutates it via .set()).
    getDark = () => {
        this.material.uniforms.pColor.value.set(0.0, 0.0, 0.0);
        this.material.uniforms.uLabDarkPass.value = 1.0;
    }

    restoreColor = () => {
        this.material.uniforms.pColor.value.set(0.5, 0.5, 0.5);
        this.material.uniforms.uLabDarkPass.value = 0.0;
    }

    fillPositionTexture = (texture) => {

        let array = texture.image.data;

        for (let i = 0, il = array.length; i < il; i += 4) {
            var x = -window.innerWidth + (Math.random() * window.innerWidth*2);//window.innerWidth - window.innerWidth * 0.2;//(Math.random() * window.innerWidth * 0.2);
            var y = -window.innerHeight + (Math.random() * window.innerHeight * 2);//0.0;//-window.innerHeight * 0.05 + Math.random() * window.innerHeight * 0.1;
            var z = -window.innerHeight + (Math.random() * window.innerHeight * 2);
            z = window.innerHeight + (Math.random() * window.innerHeight * 2);

            array[i + 0] = x;
            array[i + 1] = y;
            array[i + 2] = z;
            // .w = initial LAB age. Randomised in [0, LAB_MAXAGE) so particles start at
            // different life phases (desynchronised cycle from the first LAB entry) instead
            // of all aging in lockstep from 0. Below uLabMaxAge so none start dead. Unused
            // outside LAB (age is frozen there); only the LAB lifecycle reads it.
            array[i + 3] = Math.random() * LAB_MAXAGE;
        }
    }

    fillVelocityTexture = (texture) => {

        let array = texture.image.data;

        for (let i = 0, il = array.length; i < il; i += 4) {

            array[i + 0] = 5.0;
            array[i + 1] = 0.0;
            array[i + 2] = 0.0;
            array[i + 3] = 0.0;

        }

    }

    fillForceTexture = (texture) => {

        let array = texture.image.data;

        for (let i = 0, il = array.length; i < il; i += 4) {

            array[i + 0] = USE_RANDOM_MASS
                ? MASS_MIN + Math.random() * (MASS_MAX - MASS_MIN)
                : 1.0;
            array[i + 1] = 0.008 + Math.random() * 0.010;       // lifeSpeed (age rate) — read reliably via velocity.w
            array[i + 2] = Math.random();                       // z = seed (legacy; no longer gates respawn)
            array[i + 3] = LAB_MAXAGE;                           // w = maxAge (legacy; lifecycle now uses the uLabMaxAge uniform)

        }

    }

    initValues = (w_, resolution) => {

        const total = resolution * resolution;

        const uvs = new Float32Array(total * 2);

        let i2 = 0;

        for (let i = 0; i < resolution; i++) {

            const uvx = i / resolution;

            for (let j = 0; j < resolution; j++) {

                const uvy = j / resolution;

                uvs[i2 + 0] = uvx;
                uvs[i2 + 1] = uvy;

                i2 += 2;

            }

        }

        const result = {
            uv: uvs
        }

        return result;
    }

    update = (time, mouse, isManifesto) => {

        this.gpuCompute.compute();

        this.velocityUniforms["time"].value = time;
        // Phase 8R-B: the position material gets the SAME clock (it was a dead JS-side uniform
        // before — never pushed, never declared in the GLSL). labCloudHome's lobe drift needs the
        // two sim programs to agree on time so respawn and containment track the same moving point.
        this.positionUniforms["time"].value = time;
        this.velocityUniforms["origin"].value = mouse;
        this.velocityUniforms["isManifesto"].value = isManifesto;
        this.velocityUniforms["isMobile"].value = true;
        this.velocityUniforms.needsUpdate = true;
        this.velocityUniforms.elementsNeedUpdate = true;

        // Fan the LAB controls (set by Sketch.update on velocityUniforms) out to the
        // position shader (respawn gate) and the render material (vertex fade) so all
        // three see the same blend + reduced-motion values. (Phase 3B.)
        const labBlend = this.velocityUniforms["uLabBlend"].value;
        const labRM = this.velocityUniforms["uLabReducedMotion"].value;
        this.positionUniforms["uLabBlend"].value = labBlend;
        this.positionUniforms["uLabReducedMotion"].value = labRM;
        this.material.uniforms["uLabBlend"].value = labBlend;
        this.material.uniforms["uLabReducedMotion"].value = labRM;

        // Phase 8R tuning scaffold (TEMPORARY — remove at sign-off). Push the live aesthetic levers
        // each frame: composition (lobes/arms/void/tiers) via pushLabComposition to BOTH sim
        // materials (identical labCloudHome), motion to velocity, lifecycle to position + render.
        const _T = labTuning;
        const _vu = this.velocityUniforms, _pu = this.positionUniforms;
        this.pushLabComposition();
        _vu["uLabContainment"].value = _T.containment;
        _vu["uLabNoise"].value.set(_T.noiseScale, _T.noiseSpeed);
        _pu["uLabMaxAge"].value = _T.maxAge;
        this.material.uniforms["uLabMaxAge"].value = _T.maxAge;
        // Phase 8R render levers (v2): tier fractions, per-tier size centers +/- jitter,
        // tiered luminance floor (dust/medium lifted, anchors restrained), soft-disc profile,
        // depth dim window derived from viewport height (resize-safe), anchor bloom seed.
        const _mu = this.material.uniforms;
        _mu["uLabTierFrac"].value.set(_T.dustFrac, 1.0 - _T.anchorFrac);
        _mu["uLabSizeLo"].value.set(_T.dustSize - 0.2, _T.medSize - 0.35, _T.anchorSize - 0.55);
        _mu["uLabSizeHi"].value.set(_T.dustSize + 0.2, _T.medSize + 0.35, _T.anchorSize + 0.55);
        _mu["uLabLum"].value.set(_T.baseLum * 0.8, _T.baseLum * 1.15, _T.baseLum * 1.6);
        _mu["uLabDisc"].value.set(_T.discCore, _T.discEdge);
        const _H = window.innerHeight;
        _mu["uLabDepthDim"].value.set(0.6 * _H, 1.4 * _H, _T.depthDim);
        _mu["uLabAnchorGlow"].value = _T.anchorGlow;

        let varPosition = this.gpuCompute.getCurrentRenderTarget(this.positionVariable).texture;
        let varVelocity = this.gpuCompute.getCurrentRenderTarget(this.velocityVariable).texture;

        let t = time * 0.1;
        this.material.uniforms.time.value = t;
        this.material.uniforms.texturePosition.value = varPosition;
        this.material.uniforms.textureVelocity.value = varVelocity;
        this.mesh.customDepthMaterial.uniforms.texturePosition.value = varPosition;

    }

}

export default ParticleSystem;
